// スタッフ編集フォームコンポーネント
// 新規作成・編集両対応のモーダルフォーム

import React, { useState, useEffect } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Alert,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  FormGroup,
  LinearProgress,
  IconButton,
} from '@mui/material';
import {
  Person as UserIcon,
  School as EducationIcon,
  AccessTime as ClockIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

import { StaffInfo } from '../../types/staff';
import { useStaffInfo } from '../../hooks/useStaffInfo';

interface StaffEditorProps {
  open: boolean;
  onClose: () => void;
  staff?: StaffInfo | null;
  onSave?: (staff: StaffInfo) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`staff-editor-tabpanel-${index}`}
      aria-labelledby={`staff-editor-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
};

// 利用可能なスキル・資格のマスターデータ
const availableSkills = [
  'ドライヘッドスパ',
  'ヘッドマッサージ',
  'フェイシャル',
  'アロマトリートメント',
  'アイヘッドケア',
  'ボディケア',
  'リフレクソロジー',
  'リンパマッサージ',
  'ホットストーン',
  'カウンセリング',
];

const availableLicenses = [
  '美容師免許',
  'アロマ検定1級',
  'アロマ検定2級',
  'リンパケア検定',
  'リフレクソロジー検定',
  'ヘッドスパ検定',
  '衛生管理者',
];

const availableSpecialties = [
  'ヘッドスパ専門',
  'リラクゼーション',
  'アロマセラピー',
  '眼精疲労ケア',
  'ストレスケア',
  '美容フェイシャル',
  'デトックスケア',
];

const StaffEditor: React.FC<StaffEditorProps> = ({
  open,
  onClose,
  staff,
  onSave,
}) => {
  const { createStaff, updateStaff, validateStaffData, validationErrors, isLoading } = useStaffInfo();
  
  const [currentTab, setCurrentTab] = useState(0);
  const [formData, setFormData] = useState<Partial<StaffInfo>>({
    basicInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      hireDate: new Date(),
      role: 'staff',
      status: 'active',
    },
    qualifications: {
      licenses: [],
      skills: [],
      experience: 0,
      specialties: [],
    },
    workSchedule: {
      availableDays: ['mon', 'tue', 'wed', 'thu', 'fri'],
      workingHours: { start: '09:00', end: '18:00' },
      breakTime: { start: '12:00', end: '13:00' },
    },
    preferences: {
      maxClientsPerDay: 6,
      preferredServices: [],
      notes: '',
    },
  });

  const [localErrors, setLocalErrors] = useState<string[]>([]);
  const isEditMode = !!staff;

  // フォームデータの初期化
  useEffect(() => {
    if (staff) {
      setFormData(staff);
    } else {
      // 新規作成時の初期値
      setFormData({
        basicInfo: {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          hireDate: new Date(),
          role: 'staff',
          status: 'active',
        },
        qualifications: {
          licenses: [],
          skills: [],
          experience: 0,
          specialties: [],
        },
        workSchedule: {
          availableDays: ['mon', 'tue', 'wed', 'thu', 'fri'],
          workingHours: { start: '09:00', end: '18:00' },
          breakTime: { start: '12:00', end: '13:00' },
        },
        preferences: {
          maxClientsPerDay: 6,
          preferredServices: [],
          notes: '',
        },
      });
    }
    setCurrentTab(0);
    setLocalErrors([]);
  }, [staff, open]);

  // フォーム入力ハンドラー
  const handleBasicInfoChange = (field: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      basicInfo: {
        ...prev.basicInfo!,
        [field]: value,
      },
    }));
  };

  const handleQualificationsChange = (field: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      qualifications: {
        ...prev.qualifications!,
        [field]: value,
      },
    }));
  };

  const handleWorkingHoursChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      workSchedule: {
        ...prev.workSchedule!,
        workingHours: {
          ...prev.workSchedule!.workingHours,
          [field]: value,
        },
      },
    }));
  };

  const handleBreakTimeChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      workSchedule: {
        ...prev.workSchedule!,
        breakTime: {
          ...prev.workSchedule!.breakTime,
          [field]: value,
        },
      },
    }));
  };

  const handlePreferencesChange = (field: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences!,
        [field]: value,
      },
    }));
  };

  // 勤務曜日変更ハンドラー
  const handleAvailableDaysChange = (day: string, checked: boolean) => {
    setFormData(prev => {
      const currentDays = prev.workSchedule?.availableDays || [];
      const newDays = checked
        ? [...currentDays, day]
        : currentDays.filter(d => d !== day);
      
      return {
        ...prev,
        workSchedule: {
          ...prev.workSchedule!,
          availableDays: newDays,
        },
      };
    });
  };

  // 保存処理
  const handleSave = async () => {
    setLocalErrors([]);
    
    // バリデーション
    const validation = validateStaffData(formData);
    if (!validation.isValid) {
      setLocalErrors(validation.errors.map(err => err.message));
      return;
    }

    try {
      let success = false;
      
      if (isEditMode && staff) {
        success = await updateStaff(staff.id, formData);
      } else {
        const newId = await createStaff(formData as Omit<StaffInfo, 'id' | 'createdAt' | 'updatedAt'>);
        success = !!newId;
      }

      if (success) {
        if (onSave) {
          onSave(formData as StaffInfo);
        }
        onClose();
      }
    } catch (error) {
      setLocalErrors(['保存中にエラーが発生しました']);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // 曜日のマスターデータ
  const weekDays = [
    { key: 'mon', label: '月曜日' },
    { key: 'tue', label: '火曜日' },
    { key: 'wed', label: '水曜日' },
    { key: 'thu', label: '木曜日' },
    { key: 'fri', label: '金曜日' },
    { key: 'sat', label: '土曜日' },
    { key: 'sun', label: '日曜日' },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {isEditMode ? 'スタッフ情報編集' : '新規スタッフ登録'}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        {isLoading && <LinearProgress sx={{ mt: 1 }} />}
      </DialogTitle>

      <DialogContent dividers>
        {/* エラー表示 */}
        {(localErrors.length > 0 || validationErrors?.errors.length) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>入力内容を確認してください:</Typography>
            <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
              {localErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
              {validationErrors?.errors.map((error, index) => (
                <li key={`validation-${index}`}>{error.message}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* タブ */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab
              label="基本情報"
              icon={<UserIcon />}
              iconPosition="start"
            />
            <Tab
              label="資格・スキル"
              icon={<EducationIcon />}
              iconPosition="start"
            />
            <Tab
              label="勤務時間"
              icon={<ClockIcon />}
              iconPosition="start"
            />
            <Tab
              label="設定"
              icon={<SettingsIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* 基本情報タブ */}
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="姓"
                value={formData.basicInfo?.lastName || ''}
                onChange={(e) => handleBasicInfoChange('lastName', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="名"
                value={formData.basicInfo?.firstName || ''}
                onChange={(e) => handleBasicInfoChange('firstName', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="メールアドレス"
                type="email"
                value={formData.basicInfo?.email || ''}
                onChange={(e) => handleBasicInfoChange('email', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="電話番号"
                value={formData.basicInfo?.phone || ''}
                onChange={(e) => handleBasicInfoChange('phone', e.target.value)}
                placeholder="090-1234-5678"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="入社日"
                type="date"
                value={formData.basicInfo?.hireDate ? 
                  formData.basicInfo.hireDate.toISOString().split('T')[0] : ''}
                onChange={(e) => handleBasicInfoChange('hireDate', new Date(e.target.value))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>役職</InputLabel>
                <Select
                  value={formData.basicInfo?.role || 'staff'}
                  label="役職"
                  onChange={(e) => handleBasicInfoChange('role', e.target.value)}
                >
                  <MenuItem value="trainee">🎓 研修生</MenuItem>
                  <MenuItem value="staff">🧑‍💼 スタッフ</MenuItem>
                  <MenuItem value="manager">👤 マネージャー</MenuItem>
                  <MenuItem value="owner">👑 オーナー</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>ステータス</InputLabel>
                <Select
                  value={formData.basicInfo?.status || 'active'}
                  label="ステータス"
                  onChange={(e) => handleBasicInfoChange('status', e.target.value)}
                >
                  <MenuItem value="active">勤務中</MenuItem>
                  <MenuItem value="vacation">休暇中</MenuItem>
                  <MenuItem value="inactive">休職中</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </TabPanel>

        {/* 資格・スキルタブ */}
        <TabPanel value={currentTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={availableLicenses}
                value={formData.qualifications?.licenses || []}
                onChange={(_event, newValue) => handleQualificationsChange('licenses', newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="資格・免許"
                    placeholder="資格を選択してください"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={availableSkills}
                value={formData.qualifications?.skills || []}
                onChange={(_event, newValue) => handleQualificationsChange('skills', newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="スキル・技能"
                    placeholder="スキルを選択してください"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="経験年数"
                type="number"
                value={formData.qualifications?.experience || 0}
                onChange={(e) => handleQualificationsChange('experience', Number(e.target.value))}
                inputProps={{ min: 0, max: 50, step: 0.5 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={availableSpecialties}
                value={formData.qualifications?.specialties || []}
                onChange={(_event, newValue) => handleQualificationsChange('specialties', newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="専門分野"
                    placeholder="専門分野を選択してください"
                  />
                )}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* 勤務時間タブ */}
        <TabPanel value={currentTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>勤務可能曜日</Typography>
              <FormGroup row>
                {weekDays.map((day) => (
                  <FormControlLabel
                    key={day.key}
                    control={
                      <Checkbox
                        checked={formData.workSchedule?.availableDays?.includes(day.key) || false}
                        onChange={(e) => handleAvailableDaysChange(day.key, e.target.checked)}
                      />
                    }
                    label={day.label}
                  />
                ))}
              </FormGroup>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="勤務開始時間"
                type="time"
                value={formData.workSchedule?.workingHours?.start || '09:00'}
                onChange={(e) => handleWorkingHoursChange('start', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="勤務終了時間"
                type="time"
                value={formData.workSchedule?.workingHours?.end || '18:00'}
                onChange={(e) => handleWorkingHoursChange('end', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="休憩開始時間"
                type="time"
                value={formData.workSchedule?.breakTime?.start || '12:00'}
                onChange={(e) => handleBreakTimeChange('start', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="休憩終了時間"
                type="time"
                value={formData.workSchedule?.breakTime?.end || '13:00'}
                onChange={(e) => handleBreakTimeChange('end', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* 設定タブ */}
        <TabPanel value={currentTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="1日最大顧客数"
                type="number"
                value={formData.preferences?.maxClientsPerDay || 6}
                onChange={(e) => handlePreferencesChange('maxClientsPerDay', Number(e.target.value))}
                inputProps={{ min: 1, max: 20 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={availableSkills}
                value={formData.preferences?.preferredServices || []}
                onChange={(_event, newValue) => handlePreferencesChange('preferredServices', newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="得意メニュー"
                    placeholder="得意なメニューを選択してください"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="特記事項・メモ"
                value={formData.preferences?.notes || ''}
                onChange={(e) => handlePreferencesChange('notes', e.target.value)}
                placeholder="このスタッフに関する特記事項やメモを記載してください"
              />
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading}
          startIcon={<CheckIcon />}
        >
          {isEditMode ? '更新' : '作成'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaffEditor;